import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, HardHat, MoreVertical, Pencil, Trash2, Wallet, FileDown, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function Ouvriers() {
  const [items, setItems] = useState(null);
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [week, setWeek] = useState(new Date().toISOString().slice(0, 10));
  const [matrix, setMatrix] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payDlg, setPayDlg] = useState(null);
  const [pdf, setPdf] = useState(null);

  const loadWorkers = () => api.get("/workers").then((r) => setItems(r.data));
  const loadMatrix = (w) => api.get(`/worker-attendance?week_start=${w}`).then((r) => setMatrix(r.data));
  const loadPayments = () => api.get("/worker-payments").then((r) => setPayments(r.data)).catch(() => setPayments([]));
  useEffect(() => {
    loadWorkers(); loadPayments();
    api.get("/client-projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/cash-accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadMatrix(week); }, [week]);

  const empty = () => ({ nom: "", telephone: "", adresse: "", specialite: "", taux_journalier: 0, projet_id: "", client_nom: "", statut: "Actif", observations: "" });
  const openNew = () => { setForm(empty()); setOpen(true); };
  const openEdit = (w) => { setForm({ ...empty(), ...w }); setOpen(true); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); if (!form.nom) { toast.error("Nom requis"); return; }
    const proj = projects.find((p) => p.id === form.projet_id);
    const payload = { ...form, projet_nom: proj ? proj.nom : "" };
    try { if (form.id) await api.put(`/workers/${form.id}`, payload); else await api.post("/workers", payload); toast.success("Ouvrier enregistré"); setOpen(false); loadWorkers(); loadMatrix(week); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cet ouvrier ?")) return; await api.delete(`/workers/${id}`); toast.success("Supprimé"); loadWorkers(); loadMatrix(week); };

  const setCell = async (workerId, date, v) => {
    const present = v !== "__absent__";
    const projId = (v === "__present__" || v === "__absent__") ? "" : v;
    const proj = projects.find((p) => p.id === projId);
    try { await api.post("/worker-attendance", { worker_id: workerId, date, present, projet_id: projId, projet_nom: proj ? proj.nom : "" }); loadMatrix(week); }
    catch (e) { toast.error(apiErr(e)); loadMatrix(week); }
  };
  const genPay = async (workerId) => {
    try { await api.post("/worker-payments/generate", { worker_id: workerId, week_start: week }); toast.success("Demande de paiement générée"); loadPayments(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const shiftWeek = (n) => { const d = new Date(week); d.setDate(d.getDate() + n * 7); setWeek(d.toISOString().slice(0, 10)); };

  const doPay = async () => {
    try { await api.post(`/worker-payments/${payDlg.id}/pay`, { montant: Number(payDlg.montant), mode: payDlg.mode, account_id: payDlg.account_id, date: payDlg.date }); toast.success("Paiement enregistré"); setPayDlg(null); loadPayments(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const list = items || [];
  return (
    <div>
      <PageHeader title="Ouvriers" subtitle="Gestion des ouvriers, pointage journalier & demandes de paiement">
        <Button onClick={openNew} data-testid="add-worker" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvel ouvrier</Button>
      </PageHeader>

      <Tabs defaultValue="liste">
        <TabsList><TabsTrigger value="liste" data-testid="tab-workers">Ouvriers</TabsTrigger><TabsTrigger value="pointage" data-testid="tab-pointage">Pointage & Paiements</TabsTrigger></TabsList>

        <TabsContent value="liste">
          {items === null ? <Loader /> : list.length === 0 ? (
            <EmptyState icon={HardHat} title="Aucun ouvrier" subtitle="Ajoutez vos ouvriers et affectez-les aux projets." />
          ) : (
            <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>N°</TableHead><TableHead>Nom</TableHead><TableHead>Spécialité</TableHead><TableHead>Téléphone</TableHead><TableHead>Projet / Client</TableHead><TableHead>Taux/jour</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {list.map((w) => (
                    <TableRow key={w.id} className="hover:bg-slate-50" data-testid={`worker-row-${w.id}`}>
                      <TableCell className="font-mono text-xs text-slate-500">{w.numero}</TableCell>
                      <TableCell className="font-semibold">{w.nom}</TableCell>
                      <TableCell className="text-sm text-slate-600">{w.specialite || "—"}</TableCell>
                      <TableCell className="text-sm">{w.telephone || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-600">{w.projet_nom || w.client_nom || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtMoney(w.taux_journalier)}</TableCell>
                      <TableCell><StatusBadge value={w.statut} /></TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`worker-menu-${w.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(w)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => remove(w.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div></div>
          )}
        </TabsContent>

        <TabsContent value="pointage">
          <div className="flex items-center gap-3 mb-3">
            <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="text-sm font-medium">Semaine du {matrix ? fmtDate(matrix.week_start) : "…"} au {matrix ? fmtDate(matrix.week_end) : "…"}</div>
            <Button variant="outline" size="icon" onClick={() => shiftWeek(1)} data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          {!matrix ? <Loader /> : (
            <div className="rounded-xl border bg-white overflow-hidden mb-6"><div className="overflow-x-auto scrollbar-thin">
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Ouvrier</TableHead>{matrix.days.map((d, i) => <TableHead key={d} className="text-center">{DAY_LABELS[i]}<br /><span className="text-[10px] text-slate-400">{d.slice(8)}</span></TableHead>)}<TableHead className="text-center">Jours</TableHead><TableHead className="text-center">Montant</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {matrix.rows.length === 0 ? <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-6">Aucun ouvrier</TableCell></TableRow> : matrix.rows.map((r) => (
                    <TableRow key={r.worker.id} data-testid={`att-row-${r.worker.id}`}>
                      <TableCell className="font-medium">{r.worker.nom}</TableCell>
                      {matrix.days.map((d) => (
                        <TableCell key={d} className="text-center">
                          <Select value={r.presence[d]?.projet_id || (r.presence[d]?.present ? "__present__" : "")} onValueChange={(v) => setCell(r.worker.id, d, v)}>
                            <SelectTrigger className="h-7 w-28 text-xs mx-auto" data-testid={`att-${r.worker.id}-${d}`}><SelectValue placeholder="Absent" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__absent__">Absent</SelectItem>
                              <SelectItem value="__present__">Présent (sans projet)</SelectItem>
                              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      ))}
                      <TableCell className="text-center font-mono font-bold">{r.jours}</TableCell>
                      <TableCell className="text-center font-mono text-xs">{fmtMoney(r.montant)}</TableCell>
                      <TableCell><Button size="sm" variant="outline" onClick={() => genPay(r.worker.id)} disabled={!r.jours} data-testid={`gen-pay-${r.worker.id}`}>Demande</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div></div>
          )}

          <h3 className="font-head font-semibold mb-2 text-sm">Demandes de paiement hebdomadaires (dette ouvriers)</h3>
          <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>N°</TableHead><TableHead>Ouvrier</TableHead><TableHead>Semaine</TableHead><TableHead>Jours</TableHead><TableHead>Montant</TableHead><TableHead>Payé</TableHead><TableHead>Solde</TableHead><TableHead>État</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {payments.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-6">Aucune demande</TableCell></TableRow> : payments.map((p) => (
                  <TableRow key={p.id} data-testid={`wpay-row-${p.id}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{p.numero}</TableCell>
                    <TableCell className="font-semibold">{p.worker_name}</TableCell>
                    <TableCell className="text-xs">{fmtDate(p.week_start)} → {fmtDate(p.week_end)}</TableCell>
                    <TableCell className="text-center font-mono">{p.jours}</TableCell>
                    <TableCell className="font-mono text-xs font-bold">{fmtMoney(p.montant)}</TableCell>
                    <TableCell className="font-mono text-xs text-emerald-600">{fmtMoney(p.montant_paye)}</TableCell>
                    <TableCell className={`font-mono text-xs ${p.solde > 0 ? "text-rose-600" : "text-slate-400"}`}>{fmtMoney(p.solde)}</TableCell>
                    <TableCell><StatusBadge value={p.etat} /></TableCell>
                    <TableCell className="flex gap-1">
                      {p.solde > 0 && <Button variant="ghost" size="icon" onClick={() => setPayDlg({ id: p.id, montant: p.solde, mode: "Espèces", account_id: "", date: new Date().toISOString().slice(0, 10) })} data-testid={`wpay-pay-${p.id}`}><Wallet className="h-4 w-4 text-brand-600" /></Button>}
                      <Button variant="ghost" size="icon" onClick={() => setPdf(`/worker-payments/${p.id}/pdf`)}><FileDown className="h-4 w-4 text-slate-400" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div></div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? "Modifier l'ouvrier" : "Nouvel ouvrier"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Nom *</Label><Input value={form.nom} onChange={(e) => set("nom", e.target.value)} required className="mt-1" data-testid="worker-nom" /></div>
              <div><Label className="text-xs">Téléphone</Label><Input value={form.telephone} onChange={(e) => set("telephone", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Spécialité / poste</Label><Input value={form.specialite} onChange={(e) => set("specialite", e.target.value)} className="mt-1" data-testid="worker-specialite" /></div>
              <div><Label className="text-xs">Taux journalier (FCFA)</Label><Input type="number" value={form.taux_journalier} onChange={(e) => set("taux_journalier", Number(e.target.value))} className="mt-1" data-testid="worker-taux" /></div>
              <div><Label className="text-xs">Projet / Client affecté</Label>
                <Select value={form.projet_id || ""} onValueChange={(v) => set("projet_id", v)}>
                  <SelectTrigger className="mt-1" data-testid="worker-projet"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="col-span-2"><Label className="text-xs">Adresse</Label><Input value={form.adresse} onChange={(e) => set("adresse", e.target.value)} className="mt-1" /></div>
              <DialogFooter className="col-span-2"><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button type="submit" data-testid="worker-submit" className="bg-brand-600 hover:bg-brand-700 text-white">Enregistrer</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!payDlg} onOpenChange={(o) => !o && setPayDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-head">Régler l'ouvrier</DialogTitle></DialogHeader>
          {payDlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Montant</Label><Input type="number" value={payDlg.montant} onChange={(e) => setPayDlg({ ...payDlg, montant: e.target.value })} className="mt-1" data-testid="wpay-montant" /></div>
              <div><Label className="text-xs">Caisse (décaissement)</Label>
                <Select value={payDlg.account_id} onValueChange={(v) => setPayDlg({ ...payDlg, account_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="wpay-account"><SelectValue placeholder="Sans débit de caisse" /></SelectTrigger>
                  <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.nom}</SelectItem>)}</SelectContent>
                </Select></div>
              <DialogFooter><Button variant="outline" onClick={() => setPayDlg(null)}>Annuler</Button><Button onClick={doPay} data-testid="wpay-submit" className="bg-brand-600 hover:bg-brand-700 text-white">Valider</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog open={!!pdf} onOpenChange={(o) => !o && setPdf(null)} path={pdf} filename="demande-paiement.pdf" title="Demande de paiement" />
    </div>
  );
}
